import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import upload from "../assets/ic_upload.svg";
import config from "../config";
import { SHIPPING_PLANS } from "../constants/shippingPlans";

const EditProduct = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [form, setForm] = useState({
    title: "",
    price: "",
    description: "",
    shippingType: "jewelry",
  });
  const [newImage, setNewImage] = useState(null);
  const [existingImageUrl, setExistingImageUrl] = useState("");
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [message, setMessage] = useState({ text: "", type: "" });

  useEffect(() => {
    const loadProduct = async () => {
      try {
        const res = await fetch(`${config.BASE_URL}/products/${id}`);
        const data = await res.json();

        if (!data.success || !data.data?.product) {
          setMessage({ text: data.message || "Product not found.", type: "error" });
          return;
        }

        const product = data.data.product;
        setForm({
          title: product.title || "",
          price: product.price != null ? String(product.price) : "",
          description: product.description || "",
          shippingType: product.shippingType || "jewelry",
        });
        setExistingImageUrl(`${config.BASE_URL}/uploads/${product.url}`);
      } catch (err) {
        console.error(err);
        setMessage({ text: "Failed to load product.", type: "error" });
      } finally {
        setFetching(false);
      }
    };

    loadProduct();
  }, [id]);

  const previewSrc = useMemo(() => {
    if (newImage) return URL.createObjectURL(newImage);
    return existingImageUrl;
  }, [newImage, existingImageUrl]);

  useEffect(() => {
    if (!newImage || !previewSrc.startsWith("blob:")) return;
    return () => URL.revokeObjectURL(previewSrc);
  }, [newImage, previewSrc]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setErrors((prev) => ({ ...prev, image: "Please select a valid image file." }));
      return;
    }
    setErrors((prev) => ({ ...prev, image: "" }));
    setNewImage(file);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    setMessage({ text: "", type: "" });

    const newErrors = {};
    if (!form.title.trim()) newErrors.title = "Title is required.";
    if (!form.price.trim()) newErrors.price = "Price is required.";
    if (!form.description.trim()) newErrors.description = "Description is required.";

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    const formData = new FormData();
    formData.append("title", form.title);
    formData.append("price", form.price);
    formData.append("description", form.description);
    formData.append("shippingType", form.shippingType);
    if (newImage) {
      formData.append("file", newImage);
    }

    try {
      setLoading(true);
      const res = await fetch(`${config.BASE_URL}/products/${id}`, {
        method: "PUT",
        body: formData,
      });
      const data = await res.json();
      setLoading(false);

      if (data.success) {
        setMessage({ text: "Product updated successfully!", type: "success" });
        setTimeout(() => navigate("/dashboard/product"), 1500);
      } else {
        setMessage({ text: data.message || "Update failed.", type: "error" });
      }
    } catch (err) {
      console.error(err);
      setLoading(false);
      setMessage({ text: "Something went wrong while updating.", type: "error" });
    }
  };

  if (fetching) {
    return (
      <div className="min-h-screen bg-[#211436] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-purple-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#211436] flex flex-col items-center justify-center p-8 relative text-white">
      {loading && (
        <div className="absolute inset-0 bg-black bg-opacity-60 flex flex-col items-center justify-center z-50">
          <div className="w-12 h-12 border-4 border-purple-500 border-t-transparent rounded-full animate-spin mb-4" />
          <p className="text-white text-lg font-medium">Updating product...</p>
        </div>
      )}

      <form
        onSubmit={handleUpdate}
        className="bg-[#2b145c] p-10 rounded-2xl shadow-xl w-full max-w-4xl flex flex-col md:flex-row gap-8"
      >
        <label
          htmlFor="imageUpload"
          className="flex-1 flex flex-col justify-center items-center border-2 border-dashed border-gray-400 rounded-xl cursor-pointer p-8 hover:border-purple-400 transition min-h-[348px]"
        >
          {previewSrc ? (
            <img
              src={previewSrc}
              alt="Preview"
              className="w-full h-48 object-cover rounded-lg"
            />
          ) : (
            <>
              <img src={upload} alt="Upload" className="w-16 h-16 mb-4 opacity-80" />
              <p className="text-gray-300 font-medium">Replace Product Image (optional)</p>
            </>
          )}
          <p className="text-gray-400 text-xs mt-2">Click to choose a new file</p>
          <input
            id="imageUpload"
            type="file"
            accept="image/*"
            onChange={handleFileChange}
            className="hidden"
          />
          {errors.image && (
            <p className="text-red-400 text-sm mt-2">{errors.image}</p>
          )}
        </label>

        <div className="flex-1 flex flex-col gap-6">
          <div>
            <label className="block text-sm text-gray-300 mb-2">Title</label>
            <input
              type="text"
              name="title"
              value={form.title}
              onChange={handleChange}
              className="w-full p-3 rounded-sm bg-[#482772] text-white border-none focus:outline-none focus:ring-2 focus:ring-purple-500"
              placeholder="Enter product title"
            />
            {errors.title && (
              <p className="text-red-400 text-sm mt-1">{errors.title}</p>
            )}
          </div>

          <div>
            <label className="block text-sm text-gray-300 mb-2">Price</label>
            <input
              type="number"
              name="price"
              value={form.price}
              onChange={handleChange}
              className="w-full p-3 rounded-sm bg-[#482772] text-white border-none focus:outline-none focus:ring-2 focus:ring-purple-500"
              placeholder="Enter product price"
            />
            {errors.price && (
              <p className="text-red-400 text-sm mt-1">{errors.price}</p>
            )}
          </div>

          <div>
            <label className="block text-sm text-gray-300 mb-2">Shipping type</label>
            <select
              name="shippingType"
              value={form.shippingType}
              onChange={handleChange}
              className="w-full p-3 rounded-sm bg-[#482772] text-white border-none focus:outline-none focus:ring-2 focus:ring-purple-500"
            >
              {SHIPPING_PLANS.map((plan) => (
                <option key={plan.value} value={plan.value}>
                  {plan.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm text-gray-300 mb-2">Description</label>
            <textarea
              name="description"
              value={form.description}
              onChange={handleChange}
              rows={4}
              className="w-full p-3 rounded-sm bg-[#482772] text-white border-none focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none"
              placeholder="Enter product description"
            />
            {errors.description && (
              <p className="text-red-400 text-sm mt-1">{errors.description}</p>
            )}
          </div>

          <div className="flex gap-3 mt-2">
            <button
              type="button"
              onClick={() => navigate("/dashboard/product")}
              className="flex-1 py-3 bg-gray-600 hover:bg-gray-700 text-white font-semibold rounded-full transition"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 py-3 bg-purple-600 hover:bg-purple-700 disabled:opacity-60 text-white font-semibold rounded-full transition"
            >
              {loading ? "Saving..." : "Update Product"}
            </button>
          </div>

          {message.text && (
            <p
              className={`mt-3 text-sm ${
                message.type === "error" ? "text-red-400" : "text-green-400"
              }`}
            >
              {message.text}
            </p>
          )}
        </div>
      </form>
    </div>
  );
};

export default EditProduct;
